import ThemedSelect from "../components/shared/ThemedSelect.jsx";
import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import { ReportMasthead } from "../components/reports/ReportDocument.jsx";
import RulesHelp from "../components/shared/RulesHelp.jsx";
import { getPredictiveReport, listEntityRows, listUsers } from "../services/apiClient.js";
import { formatDate, todayInput } from "../utils/date.js";
import "../styles/home.css";

// Predictive Invoicing (legacy PredictiveInvoices.aspx): expected invoicing per
// PM per month, from each job's remaining uninvoiced value × its expected-date
// percentages. Overdue = expected dates already past (1.0 left these under
// stale month labels); Not Set = no dates yet (100% of remaining) — both are
// calls to action, so they lead the columns. On Hold sits last, out of the
// live months. Division/PM filter server-side; the month picker narrows locally.
//
// Dressed as a DOCUMENT (report-document.css), and LANDSCAPE rather than
// portrait: the matrix grows a column per month that has expected money in it,
// so its width is set by the data and not by us — an upright sheet starves it as
// soon as the pipeline runs a few months out. No Show Columns for the same
// reason: there is no fixed list of columns to offer.

const nzd = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 0 });
const money = (v) => (v == null ? "—" : nzd.format(Number(v)));

function toCsv(rows) {
  const cols = [
    ["Claim #", (r) => r.claim_number],
    ["Client", (r) => r.client_name || ""],
    ["Project Co-Ordinator", (r) => r.pm_name],
    ["Quote Value", (r) => r.total_value],
    ["Invoiced", (r) => r.invoiced],
    ["Remaining", (r) => r.remaining],
    ["%", (r) => r.percentage],
    ["Expected", (r) => r.expected_amount],
    ["Expected Date", (r) => r.expected_date || ""],
    ["Bucket", (r) => r.bucket_label],
  ];
  const esc = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [cols.map(([h]) => h).join(","), ...rows.map((r) => cols.map(([, f]) => esc(f(r))).join(","))].join("\n");
}

function PredictiveInvoicingPage() {
  const [report, setReport] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [users, setUsers] = useState([]);
  const [divisionId, setDivisionId] = useState("");
  const [pmId, setPmId] = useState("");
  const [bucket, setBucket] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    listEntityRows("divisions").then(setDivisions).catch(() => {});
    listUsers({ pco: true, activeOnly: true }).then(setUsers).catch(() => {});
  }, []);

  useEffect(() => {
    setError("");
    setReport(null);
    getPredictiveReport({ division_id: divisionId, project_coordinator_id: pmId })
      .then(setReport)
      .catch((err) => setError(err.response?.data?.error || "Unable to load the predictive report."));
  }, [divisionId, pmId]);

  // Month narrowing is local: subset the columns/rows of the loaded report.
  const view = useMemo(() => {
    if (!report) return null;
    if (!bucket) return report;
    const buckets = report.buckets.filter((b) => b.key === bucket);
    const rows = report.rows.filter((r) => r.bucket === bucket);
    const pms = report.matrix.pms
      .filter((pm) => pm.cells[bucket] != null)
      .map((pm) => ({ ...pm, total: pm.cells[bucket] }));
    return {
      buckets,
      rows,
      matrix: { pms, totals: { [bucket]: report.matrix.totals[bucket] }, grand: report.matrix.totals[bucket] || 0 },
    };
  }, [report, bucket]);

  function exportCsv() {
    const blob = new Blob([toCsv(view.rows)], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "predictive-invoicing.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  }

  // What this copy covers, for the masthead: the narrowest filter wins, because
  // that is what the reader is looking at.
  const nameOf = (list, id, field = "name") =>
    (id && (list || []).find((r) => String(r.id) === String(id))?.[field]) || null;
  const scopeLabel =
    nameOf(users, pmId, "full_name") || nameOf(divisions, divisionId) || "All project co-ordinators";
  const bucketLabel = (report?.buckets || []).find((b) => b.key === bucket)?.label;

  return (
    <section className="page-panel">
      {/* App chrome: the document carries its own masthead (report-document.css). */}
      <div className="d-flex flex-wrap align-items-center column-gap-4 row-gap-2 no-print">
        <div className="page-heading">
          <h2>Predictive Invoicing <RulesHelp topic="predictiveInvoicing" /></h2>
        </div>
        <div className="d-flex align-items-center gap-2 ms-auto">
          <ThemedSelect size="sm" style={{ width: 160 }} value={divisionId} onChange={(e) => setDivisionId(e.target.value)}>
            <option value="">All divisions</option>
            {divisions.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </ThemedSelect>
          <ThemedSelect size="sm" style={{ width: 180 }} value={pmId} onChange={(e) => setPmId(e.target.value)}>
            <option value="">All project co-ordinators</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.full_name}</option>
            ))}
          </ThemedSelect>
          <ThemedSelect size="sm" style={{ width: 160 }} value={bucket} onChange={(e) => setBucket(e.target.value)}>
            <option value="">All months</option>
            {(report?.buckets || []).map((b) => (
              <option key={b.key} value={b.key}>{b.label}</option>
            ))}
          </ThemedSelect>
          <Button size="sm" variant="outline-secondary" onClick={exportCsv} disabled={!view || view.rows.length === 0}>
            Export CSV
          </Button>
        </div>
      </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {!view ? (
        <p className="text-muted">Loading…</p>
      ) : view.rows.length === 0 ? (
        <p className="text-muted">Nothing left to invoice for this selection.</p>
      ) : (
        <div className="report-doc report-doc-wide border rounded">
          <ReportMasthead
            report="Predictive Invoicing"
            title={scopeLabel}
            subtitle={`Grayburn Property Services · ${bucketLabel || "all expected months"} · as at ${formatDate(todayInput())}`}
          />
          <div className="report-body">
          {/* PM × month expected-invoicing matrix (the 1.0 summary).

              Headed with a BARE .report-block-title rather than wrapped in a
              <ReportBlock>: a block is break-inside: avoid, which is right for a
              handful of figures and wrong for a table that can run to pages —
              an unbreakable table gets pushed whole to sheet two. */}
          <h3 className="report-block-title">Expected invoicing by project co-ordinator</h3>
          <div className="table-responsive mb-3">
            <Table striped bordered hover size="sm" className="mb-0 align-middle report-table">
              <thead className="table-surface">
                <tr>
                  <th>Project Co-Ordinator</th>
                  {view.buckets.map((b) => (
                    <th key={b.key} className="text-end" style={{ minWidth: 110 }}>{b.label}</th>
                  ))}
                  <th className="text-end" style={{ minWidth: 110 }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {view.matrix.pms.map((pm) => (
                  <tr key={pm.pm_id ?? "none"}>
                    <td className="text-nowrap">{pm.pm_name}</td>
                    {view.buckets.map((b) => (
                      <td key={b.key} className="text-end">{pm.cells[b.key] != null ? money(pm.cells[b.key]) : "—"}</td>
                    ))}
                    <td className="text-end fw-semibold">{money(pm.total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="table-surface">
                <tr>
                  <td className="fw-semibold">Total</td>
                  {view.buckets.map((b) => (
                    <td key={b.key} className="text-end fw-semibold">{money(view.matrix.totals[b.key] || 0)}</td>
                  ))}
                  <td className="text-end fw-semibold">{money(view.matrix.grand)}</td>
                </tr>
              </tfoot>
            </Table>
          </div>

          {/* The jobs behind the numbers — one row per job × expected date. */}
          <h3 className="report-block-title">Jobs behind the figures</h3>
          <Table striped bordered hover responsive size="sm" className="mb-0 align-middle report-table">
            <thead className="table-surface">
              <tr>
                <th className="text-nowrap">Claim #</th>
                <th>Client</th>
                <th>Project Co-Ordinator</th>
                <th className="text-end">Quote Value</th>
                <th className="text-end">Invoiced</th>
                <th className="text-end">Remaining</th>
                <th className="text-end">%</th>
                <th className="text-end">Expected</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {view.rows.map((r, i) => (
                <tr key={`${r.claim_id}-${r.expected_date || "none"}-${i}`}>
                  <td className="text-nowrap">
                    <Link to={`/claims/${r.claim_number}?tab=invoicing`}>{r.claim_number}</Link>
                  </td>
                  <td className="text-truncate" style={{ maxWidth: 200 }} data-hint={r.client_name || undefined}>
                    {r.client_name || "-"}
                  </td>
                  <td className="text-nowrap">{r.pm_name}</td>
                  <td className="text-end text-nowrap">{money(r.total_value)}</td>
                  <td className="text-end text-nowrap">{money(r.invoiced)}</td>
                  <td className="text-end text-nowrap">{money(r.remaining)}</td>
                  <td className="text-end">{r.percentage}%</td>
                  <td className="text-end text-nowrap fw-semibold">{money(r.expected_amount)}</td>
                  <td className="text-nowrap">{r.bucket_label}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          </div>
        </div>
      )}
    </section>
  );
}

export default PredictiveInvoicingPage;
